/**
 * Approval Modal Component
 * @description 승인/거부 모달 - 코멘트 입력 지원
 */

import { useState } from 'react';
import {
  Button, Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, Textarea
} from '@shared/ui/primitives';
import { CheckCircle, XCircle } from 'lucide-react';

interface ApprovalModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (comment?: string) => Promise<void>;
    action: 'approve' | 'reject';
    ticketId: string;
}

export const ApprovalModal: React.FC<ApprovalModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    action,
    ticketId,
}) => {
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        try {
            setIsLoading(true);
            await onConfirm(comment.trim() || undefined);
            setComment('');
            onClose();
        } catch (error) {
            console.error('Approval action failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setComment('');
            onClose();
        }
    };

    const isApprove = action === 'approve';

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {isApprove ? (
                            <>
                                <CheckCircle className="w-5 h-5 text-green-500" />
                                티켓 승인
                            </>
                        ) : (
                            <>
                                <XCircle className="w-5 h-5 text-red-500" />
                                티켓 거부
                            </>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        {isApprove
                            ? '이 티켓을 승인하시겠습니까?'
                            : '이 티켓을 거부하시겠습니까?'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                            티켓 ID: <span className="font-mono">{ticketId}</span>
                        </p>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            코멘트 {!isApprove && <span className="text-danger">(필수)</span>}
                        </label>
                        <Textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={
                                isApprove
                                    ? '승인 코멘트를 입력하세요 (선택사항)'
                                    : '거부 사유를 입력하세요'
                            }
                            rows={4}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        취소
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={isLoading || (!isApprove && !comment.trim())}
                        variant={isApprove ? 'default' : 'destructive'}
                    >
                        {isLoading
                            ? '처리 중...'
                            : isApprove
                                ? '승인'
                                : '거부'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

ApprovalModal.displayName = 'ApprovalModal';
